import { Chip } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BellRing } from 'lucide-react'
import type { CaseFollowupsOut, FollowupRowOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { useMe } from '../auth/session'
import { caseFollowupsQuery } from './api'
import type { ReminderTab } from './reminders-panel'

/**
 * The waiting-on-customer follow-ups on the case's customer pane (#216): every schedule, send, skip, stop and close
 * proposal as one line ("Patchbay reminded Jayden Duguay we're waiting on a response · 2 of 3 · 10:00 AM"), plus
 * what happens next. Quiet when nothing was ever scheduled; a failed read says so instead of showing nothing.
 */

const TONE: Record<FollowupRowOut['kind'], 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  scheduled: 'neutral',
  drafted: 'primary',
  sent: 'success',
  skipped: 'warning',
  failed: 'danger',
  stopped: 'neutral',
  close_drafted: 'primary',
  close_proposed: 'primary',
}

function time(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const sameDay = d.toDateString() === new Date().toDateString()
  return sameDay ? formatWhen(d.toISOString(), { style: 'time' }) : formatWhen(d.toISOString(), { style: 'absolute' })
}

export function nextLine(f: CaseFollowupsOut): string | null {
  if (f.state === 'unconfigured') return 'Follow-ups are off until a waiting status is set in Settings.'
  if (f.state === 'stopped') return f.reason === 'customer_replied' ? 'Follow-ups stopped: the customer replied.' : 'Follow-ups stopped.'
  if (f.state === 'done') return null
  if (f.state === 'closing') return f.next_due_at ? `Close proposal due ${time(f.next_due_at)}.` : null
  if (f.state === 'scheduled' && f.next_due_at) {
    const shadow = f.mode === 'shadow' ? ' (shadow: drafted, not sent)' : ''
    return `Next follow-up ${f.step ?? 1} of ${f.total ?? 3}: ${time(f.next_due_at)}${shadow}.`
  }
  return null
}

/** Which Follow-ups tab a trail row opens (#233): drafts to the shadow drafts, everything else to the sent log. */
export function trailTab(kind: FollowupRowOut['kind']): ReminderTab {
  return kind === 'drafted' || kind === 'close_drafted' ? 'drafts' : kind === 'scheduled' ? 'next' : 'outcomes'
}

export function FollowupTrail({ caseId }: { caseId: string }) {
  const q = useQuery(caseFollowupsQuery(caseId))
  const me = useMe()
  // The Follow-ups screen reads a manager/cover-only API; tech and billing keep plain rows.
  const linked = me?.role === 'manager' || me?.role === 'cover'
  if (q.isPending) return null
  if (q.isError) {
    return <p className="text-xs text-muted-foreground">Couldn't read the follow-ups for this case.</p>
  }
  const f = q.data
  const next = nextLine(f)
  const rows = f.rows ?? []
  if (!rows.length && (f.state === 'idle' || f.state === 'off')) return null
  return (
    <section
      aria-label="Customer follow-ups"
      className="space-y-1 rounded-md border border-dashed border-border px-3 py-2"
      data-testid="followup-trail"
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <BellRing className="size-3.5" aria-hidden />
        Waiting-on-customer follow-ups
        {f.mode === 'shadow' ? (
          <Chip tone="primary" size="sm">
            shadow
          </Chip>
        ) : null}
        {linked ? (
          <Link to="/followups" className="ml-auto font-medium text-primary">
            All follow-ups
          </Link>
        ) : null}
      </div>
      {next ? <p className="text-xs text-subtle">{next}</p> : null}
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.event_id} className="flex items-start gap-1.5 text-xs" data-kind={r.kind}>
            {linked ? (
              <Link to="/followups" search={{ tab: trailTab(r.kind) }} title="Open in Follow-ups" className="shrink-0">
                <Chip tone={TONE[r.kind]} size="sm">
                  {r.kind.replace('_', ' ')}
                </Chip>
              </Link>
            ) : (
              <Chip tone={TONE[r.kind]} size="sm">
                {r.kind.replace('_', ' ')}
              </Chip>
            )}
            <span>
              {r.text} · <span className="text-subtle">{time(r.at)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
